import React from "react";
import { Box, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { alpha } from "@mui/material/styles";
import { useTheme } from "../theme/useTheme";
import { useAuth } from "./contexts/AuthContext.jsx";

/**
 * Componente Breadcrumb para mostrar la navegación visual
 * @param {Array} items - Array de objetos con { label: string, onClick?: function }
 * @param {boolean} compact - Solo el último nivel, con flecha al anterior si es navegable
 */
export function Breadcrumb({ items = [], compact = false }) {
  const { theme, colors, neutrals, getRoleBranding } = useTheme();
  const { user } = useAuth();
  const branding = getRoleBranding(user?.role);
  const accentColor = branding.primary || colors.primary.main;
  const accentBg = alpha(accentColor, 0.12);
  const accentBgHover = alpha(accentColor, 0.18);
  const accentDivider = alpha(accentColor, 0.6);

  if (!items || items.length === 0) return null;

  const lastIndex = items.length - 1;
  const parent = compact && lastIndex > 0 ? items[lastIndex - 1] : null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: compact ? "nowrap" : "wrap",
        minWidth: 0,
      }}
    >
      {parent?.onClick && (
        <Box
          component="button"
          type="button"
          aria-label={parent.label}
          onClick={parent.onClick}
          sx={{
            display: "flex",
            flexShrink: 0,
            padding: "4px",
            border: "none",
            background: "none",
            cursor: "pointer",
            color: accentColor,
          }}
        >
          <ChevronLeft size={16} />
        </Box>
      )}
      {items.map((item, index) => (compact && index !== lastIndex) ? null : (
        <React.Fragment key={index}>
          {!compact && index > 0 && (
            <ChevronRight
              size={14}
              color={accentDivider}
              style={{ flexShrink: 0 }}
            />
          )}
          <Box
            onClick={item.onClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
              cursor: item.onClick ? "pointer" : "default",
              padding: "4px 10px",
              borderRadius: "6px",
              backgroundColor:
                index === items.length - 1 ? accentBg : "transparent",
              transition: "all 0.2s ease",
              "&:hover": item.onClick
                ? {
                    backgroundColor:
                      index === items.length - 1 ? accentBgHover : neutrals.gray[100],
                  }
                : {},
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.8125rem",
                fontWeight: index === items.length - 1 ? 600 : 500,
                color:
                  index === items.length - 1
                    ? accentColor
                    : theme.palette.text.secondary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.label}
            </Typography>
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}
